/**
 * Build-time version stamp injected by Vite.
 * Used to detect stale app shells and log version mismatches.
 */

// Vite replaces this at build time with the actual timestamp
declare const __APP_BUILD_VERSION__: string;
export const APP_BUILD_VERSION: string =
  typeof __APP_BUILD_VERSION__ !== "undefined"
    ? __APP_BUILD_VERSION__
    : "dev-" + Date.now();

/** Persist the running version so we can compare after SW update */
const VERSION_KEY = "vantastika_app_version";
const LIVE_VERSION_META_REGEX = /<meta\s+name=["']app-version["']\s+content=["']([^"']+)["']/i;
const LIVE_VERSION_QUERY_PARAM = "__app_shell_check";
const FORCE_RELOAD_QUERY_PARAM = "__app_reload";
const VERSIONED_NAVIGATION_QUERY_PARAM = "__app_version";
const VERSIONED_SW_QUERY_PARAM = "__sw_version";
const RUNTIME_QUERY_PARAMS = [LIVE_VERSION_QUERY_PARAM, FORCE_RELOAD_QUERY_PARAM, VERSIONED_NAVIGATION_QUERY_PARAM];

function getCurrentUrl() {
  return new URL(window.location.href);
}

export function shouldDisableServiceWorkersInCurrentContext(): boolean {
  const isInIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  if (isInIframe) return true;

  const hostname = window.location.hostname;
  return hostname.includes("id-preview--") || hostname.includes("lovableproject.com");
}

export function stripRuntimeReloadParams(): void {
  try {
    const url = getCurrentUrl();
    let changed = false;

    for (const key of RUNTIME_QUERY_PARAMS) {
      if (!url.searchParams.has(key)) continue;
      url.searchParams.delete(key);
      changed = true;
    }

    if (changed) {
      window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
    }
  } catch {
    // history unavailable
  }
}

export function getVersionedServiceWorkerUrl(version: string = APP_BUILD_VERSION): string {
  const url = new URL("/sw.js", window.location.origin);
  url.searchParams.set(VERSIONED_SW_QUERY_PARAM, version);
  return url.toString();
}

export async function registerVersionedServiceWorker(
  version: string = APP_BUILD_VERSION
): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;

  try {
    return await navigator.serviceWorker.register(getVersionedServiceWorkerUrl(version), {
      scope: "/",
      updateViaCache: "none",
    });
  } catch {
    return null;
  }
}

export function forceNetworkNavigation(_version: string = APP_BUILD_VERSION): void {
  // With NetworkOnly navigation strategy in the SW, a plain reload always
  // fetches fresh HTML from the network — no cache-busting params needed.
  window.location.reload();
}

export function getStoredVersion(): string | null {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch {
    return null;
  }
}

export function storeCurrentVersion(): void {
  try {
    localStorage.setItem(VERSION_KEY, APP_BUILD_VERSION);
  } catch {
    // storage unavailable
  }
}

export async function clearAppCaches(): Promise<void> {
  try {
    const names = await window.caches?.keys();
    if (names?.length) {
      await Promise.all(names.map((name) => window.caches.delete(name)));
    }
  } catch {
    // cache API unavailable
  }
}

export async function unregisterServiceWorkers(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister().catch(() => false)));
  } catch {
    // service worker API unavailable
  }
}

export async function fetchLiveAppVersion(): Promise<string | null> {
  try {
    const url = new URL("/", window.location.origin);
    url.searchParams.set(LIVE_VERSION_QUERY_PARAM, `${Date.now()}-${APP_BUILD_VERSION}`);

    const response = await fetch(url.toString(), {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "text/html",
        "Cache-Control": "no-cache, no-store, max-age=0",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) return null;

    const html = await response.text();
    return html.match(LIVE_VERSION_META_REGEX)?.[1] ?? null;
  } catch {
    return null;
  }
}

/** Check if the running build is different from the stored one */
export function isNewVersion(): boolean {
  const stored = getStoredVersion();
  if (!stored) return false; // first visit
  return stored !== APP_BUILD_VERSION;
}
